/**
 * SDE Phase 3 (s88) — read + decrypt the .oesif Secure File back into Cat A values.
 * Wraps the secure-file lib's parseContainer; the in-memory store's loadValues consumes
 * the result. Import only from "@/lib/secure-file" (the lib's public index).
 */
import {
  parseContainer,
  MalformedContainerError,
  InvalidPassphraseError,
  CorruptedFileError,
} from "@/lib/secure-file";

export interface ParsedSecureFile {
  /** Composite-key (fieldId::rowId) → value map, ready for store.loadValues(). */
  values: Record<string, string>;
  count: number;
}

/** Read all bytes from a FileSystemFileHandle (caller ensures permission first). */
export async function readHandleBytes(handle: FileSystemFileHandle): Promise<Uint8Array> {
  const file = await handle.getFile();
  return new Uint8Array(await file.arrayBuffer());
}

/** Read all bytes from a File (the Safari/Firefox <input type="file"> path). */
export async function readFileBytes(file: File): Promise<Uint8Array> {
  return new Uint8Array(await file.arrayBuffer());
}

/**
 * Decrypt + extract the Cat A values from .oesif bytes. Throws the lib's categorized
 * errors (MalformedContainerError / InvalidPassphraseError / CorruptedFileError).
 */
export async function parseSecureFile(bytes: Uint8Array, passphrase: string): Promise<ParsedSecureFile> {
  const parsed = await parseContainer<unknown>(bytes, passphrase);
  // Phase 2 wrote `data = { version, savedAt, values }`. Tolerate a bare map for safety.
  const data = parsed.data as { values?: Record<string, string> } | Record<string, string> | null;
  let values: Record<string, string> = {};
  if (data && typeof data === "object") {
    const maybe = (data as { values?: Record<string, string> }).values;
    values = maybe && typeof maybe === "object" ? maybe : (data as Record<string, string>);
  }
  // Keep only string values.
  const clean: Record<string, string> = {};
  for (const [k, v] of Object.entries(values)) if (typeof v === "string") clean[k] = v;
  return { values: clean, count: Object.keys(clean).length };
}

export type OpenErrorKind = "wrong-passphrase" | "corrupt" | "wrong-file" | "unknown";

/** Map a thrown error to a UI category. Note (lib limit): AES-GCM can't distinguish a
 *  wrong passphrase from tampered ciphertext — both are InvalidPassphraseError. */
export function classifyOpenError(err: unknown): OpenErrorKind {
  if (err instanceof InvalidPassphraseError) return "wrong-passphrase";
  if (err instanceof CorruptedFileError) return "corrupt";
  if (err instanceof MalformedContainerError) return "wrong-file";
  return "unknown";
}
